/**
 * lazyPage — a route's page loaded on first use (v149.64).
 *
 * The app shipped every page in one 4.8 MB script (1.2 MB compressed), so
 * a visitor opening the landing page or a bio page downloaded the whole
 * admin cockpit, editors, inventory and charts before anything painted.
 * Pages wrapped here live in their own chunk, fetched the first time the
 * route is opened (then cached for a year by the build's file hashing).
 *
 *   const Orders = lazyPage(() => import("../pages/Orders"));
 *   const { MySmartQRs } = ...  →  lazyPage(() => import("../pages/MySmartQRs"), "MySmartQRs")
 *
 * Each page carries its own <Suspense>, so an app-shell layout above it
 * stays put while the chunk loads; only the page area shows the loader.
 *
 * After a deploy, an open tab still holds the old page's chunk names; the
 * first navigation to a page not yet loaded then 404s. That failure
 * reloads the tab once (the new build has the right names), instead of
 * showing the error boundary. `preload()` fetches a chunk ahead of time.
 */
import { lazy, Suspense } from "react";

const RELOAD_KEY = "lk_chunk_reload";

function isChunkError(err) {
  const s = `${err?.name || ""} ${err?.message || ""}`;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(s);
}

async function load(loader) {
  try {
    const m = await loader();
    try { sessionStorage.removeItem(RELOAD_KEY); } catch (_) { /* no storage */ }
    return m;
  } catch (err) {
    if (isChunkError(err)) {
      let already = false;
      try {
        already = sessionStorage.getItem(RELOAD_KEY) === window.location.href;
        if (!already) sessionStorage.setItem(RELOAD_KEY, window.location.href);
      } catch (_) { /* no storage: reload once anyway */ }
      if (!already) {
        window.location.reload();
        // Keep the promise pending; the page is going away.
        return new Promise(() => {});
      }
    }
    throw err;
  }
}

export function RouteLoader() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center p-6" role="status" aria-live="polite"
         data-testid="route-loading">
      <span className="inline-block w-5 h-5 rounded-full border-2 border-current border-t-transparent animate-spin opacity-60" aria-hidden />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function lazyPage(loader, exportName) {
  const pick = (m) => ({ default: exportName ? m[exportName] : m.default });
  const Inner = lazy(() => load(loader).then(pick));
  function Page(props) {
    return (
      <Suspense fallback={<RouteLoader />}>
        <Inner {...props} />
      </Suspense>
    );
  }
  Page.preload = () => loader().catch(() => undefined);
  return Page;
}
